import { useForm } from "react-hook-form";
import { Box, Button, FormControl, FormLabel, TextField, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLinkedinDeal, saveLinkedinDeal, saveLinkedinDealReset, deleteLinkedinDeal, deleteLinkedinDealReset } from "../../state/linkedinPromotion/linkedinPromotionSlice";
import { selectLinkedinPromotionState } from "../../state/linkedinPromotion/linkedinPromotionSelector";

const DEFAULT_VALUES = {
  totalSlots: 0,
  expireDate: "",
  monthlyCreditAmount: 0,
  linkedInCompanyId: "",
  linkedInCompanyName: "",
};

const LinkedinSlotsModal = ({ onClose, companyInfo }) => {
  const dispatch = useDispatch();
  const { deal, isLoading, isSaving, isSaveSuccess, isDeleting, isDeleteSuccess } = useSelector(selectLinkedinPromotionState);

  const [confirmOpen, setConfirmOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: DEFAULT_VALUES });

  // Fetch deal on mount
  useEffect(() => {
    if (companyInfo?.id) {
      dispatch(getLinkedinDeal(companyInfo.id));
    }
  }, [companyInfo?.id, dispatch]);

  // Populate form when deal is loaded
  useEffect(() => {
    if (deal) {
      reset({
        totalSlots: deal.totalSlots,
        expireDate: deal.expireDate ? deal.expireDate.split("T")[0] : "",
        monthlyCreditAmount: deal.monthlyCreditAmount,
        linkedInCompanyId: deal.linkedInCompanyId || "",
        linkedInCompanyName: deal.linkedInCompanyName || "",
      });
    }
  }, [deal, reset]);

  // Close modal on save success
  useEffect(() => {
    if (isSaveSuccess) {
      dispatch(saveLinkedinDealReset());
      onClose();
    }
  }, [isSaveSuccess, dispatch, onClose]);

  // Reset form + close on delete success
  useEffect(() => {
    if (isDeleteSuccess) {
      dispatch(deleteLinkedinDealReset());
      reset(DEFAULT_VALUES);
      onClose();
    }
  }, [isDeleteSuccess, dispatch, reset, onClose]);

  const onSubmit = (data) => {
    const payload = { ...data, companyId: companyInfo.id };
    if (deal?.id) payload.id = deal.id;
    dispatch(saveLinkedinDeal(payload));
  };

  const handleResetClick = () => setConfirmOpen(true);
  const handleConfirmClose = () => setConfirmOpen(false);

  const handleConfirmDelete = () => {
    setConfirmOpen(false);
    dispatch(deleteLinkedinDeal(deal.id));
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 1 }}>Number of Slots</FormLabel>
          <TextField
            size="small"
            type="number"
            {...register("totalSlots", {
              valueAsNumber: true,
              required: "Number of slots is required",
              min: { value: 1, message: "Must be at least 1" },
            })}
            error={!!errors.totalSlots}
            helperText={errors.totalSlots?.message}
          />
        </FormControl>

        <FormControl fullWidth>
          <FormLabel sx={{ mb: 1 }}>Contract Expiry Date</FormLabel>
          <TextField
            size="small"
            type="date"
            {...register("expireDate", { required: "Expiry date is required" })}
            InputLabelProps={{ shrink: true }}
            error={!!errors.expireDate}
            helperText={errors.expireDate?.message}
          />
        </FormControl>

        <FormControl fullWidth>
          <FormLabel sx={{ mb: 1 }}>Monthly discount deal amount for all Slots</FormLabel>
          <TextField
            size="small"
            type="number"
            {...register("monthlyCreditAmount", {
              valueAsNumber: true,
              required: "Monthly discount deal amount is required",
              min: { value: 0, message: "Must be >= 0" },
            })}
            error={!!errors.monthlyCreditAmount}
            helperText={errors.monthlyCreditAmount?.message}
          />
        </FormControl>

        <FormControl fullWidth>
          <FormLabel sx={{ mb: 1 }}>LinkedIn Company ID</FormLabel>
          <TextField
            size="small"
            {...register("linkedInCompanyId", { required: "LinkedIn Company ID is required" })}
            error={!!errors.linkedInCompanyId}
            helperText={errors.linkedInCompanyId?.message}
          />
        </FormControl>

        <FormControl fullWidth>
          <FormLabel sx={{ mb: 1 }}>LinkedIn Company Name</FormLabel>
          <TextField
            size="small"
            {...register("linkedInCompanyName", { required: "LinkedIn Company Name is required" })}
            error={!!errors.linkedInCompanyName}
            helperText={errors.linkedInCompanyName?.message}
          />
        </FormControl>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          {deal?.id && (
            <Button type="button" variant="outlined" color="error" fullWidth disabled={isDeleting || isSaving} onClick={handleResetClick}>
              {isDeleting ? "Removing..." : "Remove Promotion Deal"}
            </Button>
          )}
          <Button type="submit" variant="contained" fullWidth disabled={isSaving || isDeleting}>
            {isSaving ? "Saving..." : deal?.id ? "Update & Save" : "Confirm & Assign"}
          </Button>
        </Stack>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={handleConfirmClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Reset LinkedIn Deal?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently <strong>delete</strong> the LinkedIn deal for <strong>{companyInfo?.name}</strong> and also remove all active job promotions linked to it. This action{" "}
            <strong>cannot be undone</strong>.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleConfirmClose} variant="outlined" fullWidth>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error" fullWidth>
            Yes, Delete &amp; Reset
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LinkedinSlotsModal;
